import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const C = {
  bg: '#0A0E1A',
  surface: '#111827',
  border: '#1F2937',
  accent: '#10B981',
  text: '#F9FAFB',
  muted: '#9CA3AF',
};

function getUser() {
  try {
    return JSON.parse(localStorage.getItem('user') || 'null');
  } catch {
    return null;
  }
}

export default function MemberPortal() {
  const navigate = useNavigate();
  const user = getUser();
  const [loading, setLoading] = useState(true);
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadPortalData = async () => {
      setLoading(true);
      setError('');

      const [walletResp, txResp] = await Promise.allSettled([
        api.get('/wallet/balance'),
        api.get('/transactions?limit=8'),
      ]);

      if (walletResp.status === 'fulfilled') {
        setWallet(walletResp.value.data || null);
      }

      if (txResp.status === 'fulfilled') {
        const rows = Array.isArray(txResp.value.data) ? txResp.value.data : [];
        setTransactions(rows);
      }

      if (walletResp.status === 'rejected' && txResp.status === 'rejected') {
        setError('No se pudo cargar tu información en este momento.');
      }

      setLoading(false);
    };

    loadPortalData();
  }, []);

  const kpis = useMemo(() => {
    const total = transactions.reduce((sum, t) => sum + Number(t.amount || 0), 0);
    const completed = transactions.filter((t) => t.status === 'completed').length;

    return {
      totalMovido: total,
      transacciones: transactions.length,
      completadas: completed,
    };
  }, [transactions]);

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    navigate('/login', { replace: true });
  };

  const money = (value) => {
    const amount = Number(value || 0);
    return amount.toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <div style={{ minHeight: '100vh', background: C.bg, padding: 24, fontFamily: "'DM Sans','Segoe UI',sans-serif" }}>
      <div style={{ maxWidth: 900, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <div>
            <div style={{ fontSize: 24, color: C.text, fontWeight: 700 }}>Portal de Socio</div>
            <div style={{ fontSize: 14, color: C.muted, marginTop: 4 }}>Bienvenido{user?.username ? `, ${user.username}` : ''}</div>
          </div>
          <button
            onClick={logout}
            style={{ background: C.surface, border: `1px solid ${C.border}`, color: C.text, borderRadius: 8, padding: '10px 14px', cursor: 'pointer' }}
          >
            Cerrar sesión
          </button>
        </div>

        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, padding: 20 }}>
          <div style={{ color: C.text, fontWeight: 700, fontSize: 18, marginBottom: 14 }}>Resumen de tu cuenta</div>
          {loading ? (
            <div style={{ color: C.muted, fontSize: 14 }}>Cargando información...</div>
          ) : error ? (
            <div style={{ color: '#FCA5A5', fontSize: 14 }}>{error}</div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 18 }}>
                <Kpi label="Saldo disponible" value={`$${money(wallet?.balance)}`} />
                <Kpi label="Cuenta" value={wallet?.account_number || '—'} />
                <Kpi label="Movimientos" value={String(kpis.transacciones)} />
                <Kpi label="Completadas" value={String(kpis.completadas)} />
                <Kpi label="Total movido" value={`$${money(kpis.totalMovido)}`} />
              </div>

              <div style={{ color: C.text, fontWeight: 700, fontSize: 15, marginBottom: 10 }}>Últimos movimientos</div>
              {transactions.length === 0 ? (
                <div style={{ color: C.muted, fontSize: 14 }}>No hay movimientos recientes.</div>
              ) : (
                <div style={{ border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        {['Referencia', 'Tipo', 'Monto', 'Estado', 'Fecha'].map((h) => (
                          <th
                            key={h}
                            style={{
                              textAlign: 'left',
                              padding: '10px 12px',
                              fontSize: 11,
                              color: C.muted,
                              textTransform: 'uppercase',
                              borderBottom: `1px solid ${C.border}`,
                            }}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((tx) => (
                        <tr key={tx.transaction_id || tx.id}>
                          <td style={{ padding: '10px 12px', fontSize: 13, color: C.text, borderBottom: `1px solid ${C.border}44` }}>{tx.transaction_id || tx.id || '—'}</td>
                          <td style={{ padding: '10px 12px', fontSize: 13, color: C.text, borderBottom: `1px solid ${C.border}44` }}>{tx.transaction_type || tx.type || '—'}</td>
                          <td style={{ padding: '10px 12px', fontSize: 13, color: C.text, borderBottom: `1px solid ${C.border}44` }}>${money(tx.amount)}</td>
                          <td style={{ padding: '10px 12px', fontSize: 13, color: tx.status === 'completed' ? C.accent : C.muted, borderBottom: `1px solid ${C.border}44` }}>{tx.status || '—'}</td>
                          <td style={{ padding: '10px 12px', fontSize: 13, color: C.muted, borderBottom: `1px solid ${C.border}44` }}>{tx.created_at ? new Date(tx.created_at).toLocaleString('es-EC') : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value }) {
  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: 12, background: C.bg }}>
      <div style={{ color: C.muted, fontSize: 12, marginBottom: 4 }}>{label}</div>
      <div style={{ color: C.text, fontSize: 16, fontWeight: 700 }}>{value}</div>
    </div>
  );
}
